import React from 'react'
import { Link } from 'react-router'

import { HoldBadge } from 'components/brokers/workflows/badges/TypeBadges'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from 'components/ui/context-menu'
import { Badge, Button } from 'slab'
import { type BrokersTaskTypeSummary } from 'utils/web_api.schemas'

import { ProgressBar } from '../tasks/ProgressBar'

interface TaskTypeSummaryProps {
  taskType: BrokersTaskTypeSummary
  onNavigateToTaskType: (taskType: string) => void
  isSelected?: boolean
  onToggleSelect?: () => void
}

const TaskTypeSummary: React.FC<TaskTypeSummaryProps> = ({
  taskType,
  onNavigateToTaskType,
  isSelected = false,
  onToggleSelect,
}) => {
  const isAutomated = taskType.default_execution_method === 'automated'
  const executionMethodBadgeVariant = isAutomated ? 'info' : 'neutral'
  const executionMethodText = isAutomated ? 'Automated' : 'Manual'

  // Get categories from the task type
  const categories = taskType.categories || []

  // Map category strings to display labels
  const categoryLabels: Record<string, string> = {
    submission: 'Submission',
    payment: 'Payment',
    autopay: 'Autopay',
  }

  // Build the URL for the task type detail page
  const taskTypeUrl = `/admin/tasks/v2/${encodeURIComponent(taskType.task_type)}`

  return (
    <Card
      className={`bg-white hover:shadow-lg transition-all cursor-pointer ${
        isSelected ? 'ring-4 ring-purple-500 border-purple-500' : ''
      }`}
      onClick={onToggleSelect}
    >
      <CardHeader className='pb-4'>
        <div className='flex items-start justify-between'>
          <div className='flex-1'>
            <CardTitle className='text-lg font-semibold text-gray-900 line-clamp-2'>
              {taskType.display_name}
            </CardTitle>
            <p className='text-sm text-gray-500 mt-1 font-mono'>
              {taskType.task_type}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Task Progress Section */}
        <div className='space-y-3'>
          <div className='text-sm font-medium text-gray-700'>
            Task Progress ({taskType.total_tasks} total)
          </div>

          <ProgressBar stats={taskType.task_statistics} />
        </div>

        {/* Recent Activity */}
        {taskType.recent_activity.tasks_last_24h > 0 && (
          <div className='text-xs text-gray-500'>
            {taskType.recent_activity.tasks_last_24h} task{taskType.recent_activity.tasks_last_24h !== 1 ? 's' : ''} in last 24h
          </div>
        )}

        {/* Actions Section */}
        <div className='pt-3 border-t border-gray-100 flex justify-between items-center'>
          <div className='flex items-center gap-2 flex-wrap'>
            <Badge type={executionMethodBadgeVariant} bordered>
              {executionMethodText}
            </Badge>
            {categories.map((category: string) => (
              <Badge key={category} type='neutral'>
                {categoryLabels[category] || category}
              </Badge>
            ))}
            <HoldBadge isOnHold={taskType.has_active_hold} />
          </div>
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <Button
                variant='tertiary'
                size='small'
                onClick={(e) => {
                  e.stopPropagation()
                  onNavigateToTaskType(taskType.task_type)
                }}
              >
                View Details
              </Button>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem asChild>
                <Link to={taskTypeUrl} target='_blank' rel='noopener noreferrer'>
                  Open in New Tab
                </Link>
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        </div>
      </CardContent>
    </Card>
  )
}

export default TaskTypeSummary
